import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cdfce993-612e-4617-ac7b-89da32b19bd7/artifacts/wcwsdulx_Logo%20benchory.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-6" data-testid="reset-password-no-token">
        <div className="max-w-md w-full text-center">
          <img src={LOGO_URL} alt="BENCHORY" className="h-12 mx-auto mb-8" />
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
            <AlertCircle size={32} className="text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#1B2A4A] font-manrope mb-2">Lien invalide</h1>
            <p className="text-gray-500 text-sm mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
            <Link to="/mot-de-passe-oublie" className="text-[#B8860B] font-semibold text-sm hover:underline">
              Refaire une demande
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-6" data-testid="reset-password-success">
        <div className="max-w-md w-full text-center">
          <img src={LOGO_URL} alt="BENCHORY" className="h-12 mx-auto mb-8" />
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h1 className="text-xl font-bold text-[#1B2A4A] font-manrope mb-2">Mot de passe mis à jour</h1>
            <p className="text-gray-500 text-sm mb-6">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
            <Link to="/login" className="inline-flex items-center gap-2 bg-[#1B2A4A] text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#152039] transition-all shadow-md">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-6" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="BENCHORY" className="h-12" />
        </div>
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
          <h1 className="text-2xl font-bold text-[#1B2A4A] font-manrope mb-1" data-testid="reset-password-title">Nouveau mot de passe</h1>
          <p className="text-gray-400 text-sm mb-6">Choisissez votre nouveau mot de passe</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm" data-testid="reset-password-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Nouveau mot de passe</label>
              <input
                data-testid="reset-password-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
                placeholder="6 caractères minimum"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Confirmer le mot de passe</label>
              <input
                data-testid="reset-password-confirm"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]"
                placeholder="Retapez le mot de passe"
                required
              />
            </div>
            <button
              data-testid="reset-password-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#1B2A4A] text-white rounded-lg font-semibold text-sm hover:bg-[#152039] transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Lock size={16} /> Réinitialiser</>}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-400 hover:text-[#1B2A4A] inline-flex items-center gap-1">
              <ArrowLeft size={14} /> Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
